import * as React from 'react'
import { SxProps, Theme } from '@mui/material/styles'
import {
  FilterColumnsArgs,
  GridColDef,
  gridFilterableColumnDefinitionsSelector,
  GridFilterForm,
  GridFilterFormProps,
  GridFilterItem,
  gridFilterModelSelector,
  GridLogicOperator,
  GridPanelContent,
  GridPanelFooter,
  GridPanelWrapper,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from '@mui/x-data-grid-pro'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

export interface FilterGroup {
  label: string
  fields: string[] // column field names belonging to this group
  groupField: string // the field that represents the group in the filter model,
}

export interface GetColumnForNewFilterArgs {
  currentFilters: GridFilterItem[]
  columns: GridColDef[]
}

export interface GridFilterPanelProps
  extends Pick<GridFilterFormProps, 'logicOperators' | 'columnsSort'> {
  sx?: SxProps<Theme>
  getColumnForNewFilter?: (
    args: GetColumnForNewFilterArgs,
  ) => GridColDef['field'] | null
  filterFormProps?: Pick<
    GridFilterFormProps,
    | 'columnsSort'
    | 'deleteIconProps'
    | 'logicOperatorInputProps'
    | 'operatorInputProps'
    | 'columnInputProps'
    | 'valueInputProps'
    | 'filterColumns'
  >
  disableAddFilterButton?: boolean
  disableRemoveAllButton?: boolean
  filterGroups?: FilterGroup[]
  children?: React.ReactNode
}

const getGridFilter = (col: GridColDef): GridFilterItem => ({
  field: col.field,
  operator: col.filterOperators![0]!.value,
  id: Math.round(Math.random() * 1e5),
})

const GridGroupFilterPanel = React.forwardRef<
  HTMLDivElement,
  GridFilterPanelProps
>(function GridFilterPanel(props, ref) {
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)
  const filterableColumns = useGridSelector(
    apiRef,
    gridFilterableColumnDefinitionsSelector,
  )
  const placeholderFilter = React.useRef<GridFilterItem | null>(null)

  const {
    logicOperators = [GridLogicOperator.And, GridLogicOperator.Or],
    columnsSort,
    filterFormProps,
    getColumnForNewFilter,
    children,
    disableAddFilterButton = false,
    disableRemoveAllButton = false,
    filterGroups = [],
    ...other
  } = props
  const [expandedGroup, setExpandedGroup] = React.useState<string | null>(null)

  const applyFilter = React.useCallback(
    (item: GridFilterItem) => {
      apiRef.current.upsertFilterItem(item)
    },
    [apiRef]
  )

  const applyFilterLogicOperator = React.useCallback(
    (operator: GridLogicOperator) => {
      apiRef.current.setFilterLogicOperator(operator)
    },
    [apiRef],
  )

  const getDefaultFilter = React.useCallback((): GridFilterItem | null => {
    let nextColumnWithOperator
    if (getColumnForNewFilter && typeof getColumnForNewFilter === 'function') {
      // To allow override the column for default (first) filter
      const nextFieldName = getColumnForNewFilter({
        currentFilters: filterModel?.items || [],
        columns: filterableColumns,
      })

      if (nextFieldName === null) {
        return null
      }

      nextColumnWithOperator = filterableColumns.find(
        ({ field }) => field === nextFieldName,
      )
    } else {
      nextColumnWithOperator = filterableColumns.find(
        (colDef) => colDef.filterOperators?.length,
      )
    }

    if (!nextColumnWithOperator) {
      return null
    }

    return getGridFilter(nextColumnWithOperator)
  }, [filterModel?.items, filterableColumns, getColumnForNewFilter])

  const getNewFilter = React.useCallback((): GridFilterItem | null => {
    if (
      getColumnForNewFilter === undefined ||
      typeof getColumnForNewFilter !== 'function'
    ) {
      return getDefaultFilter()
    }

    const currentFilters = filterModel.items.length
      ? filterModel.items
      : [getDefaultFilter()].filter(Boolean)

    // If no items are there in filterModel, we have to pass defaultFilter
    const nextColumnFieldName = getColumnForNewFilter({
      currentFilters: currentFilters as GridFilterItem[],
      columns: filterableColumns,
    })

    if (nextColumnFieldName === null) {
      return null
    }

    const nextColumnWithOperator = filterableColumns.find(
      ({ field }) => field === nextColumnFieldName,
    )

    if (!nextColumnWithOperator) {
      return null
    }

    return getGridFilter(nextColumnWithOperator)
  }, [
    filterModel.items,
    filterableColumns,
    getColumnForNewFilter,
    getDefaultFilter,
  ])

  const items = React.useMemo<GridFilterItem[]>(() => {
    if (filterModel.items.length) {
      return filterModel.items
    }

    if (!placeholderFilter.current) {
      placeholderFilter.current = getDefaultFilter()
    }

    return placeholderFilter.current ? [placeholderFilter.current] : []
  }, [filterModel.items, getDefaultFilter])

  const getGroupForItem = React.useCallback(
    (item: GridFilterItem) => {
      return filterGroups.find(
        (group) =>
          group.fields.includes(item.field) || group.groupField === item.field,
      )
    },
    [filterGroups],
  )
  const getItemsForGroup = (group: FilterGroup) => {
    return items.filter((item) => group.fields.includes(item.field))
  }
  const getUngroupedItems = () => {
    const groupedFields = filterGroups.flatMap((g) => g.fields)
    return items.filter((item) => !groupedFields.includes(item.field))
  }

  const getGroupColumns = (group: FilterGroup) => {
    return filterableColumns.filter((col) => group.fields.includes(col.field))
  }

  const getUngroupedColumns = React.useCallback(() => {
    const groupedFields = filterGroups.flatMap((g) => g.fields)
    return filterableColumns.filter((col) => !groupedFields.includes(col.field))
  }, [filterGroups, filterableColumns])

  const hasMultipleFilters = items.length > 1

  const addNewFilter = () => {
    const newFilter = getNewFilter()
    if (!newFilter) {
      return
    }
    apiRef.current.upsertFilterItems([...items, newFilter])
  }

  const deleteFilter = React.useCallback(
    (item: GridFilterItem) => {
      const group = filterGroups.find((g) => g.groupField === item.field)
      if (group) {
        const fieldsToRemove = [group.groupField, ...group.fields]
        const remainingItems = items.filter(
          (i) => !fieldsToRemove.includes(i.field),
        )
        apiRef.current.setFilterModel({
          ...filterModel,
          items: remainingItems,
        })
        if (remainingItems.length === 0) {
          apiRef.current.hideFilterPanel()
        }

        return
      }
      const shouldCloseFilterPanel = items.length === 1
      apiRef.current.deleteFilterItem(item)
      if (shouldCloseFilterPanel) {
        apiRef.current.hideFilterPanel()
      }
    },
    [apiRef, filterGroups, filterModel, items],
  )

  const handleRemoveAll = () => {
    if (items.length === 1 && items[0]!.value === undefined) {
      apiRef.current.deleteFilterItem(items[0]!)
      apiRef.current.hideFilterPanel()
    }
    apiRef.current.setFilterModel({ ...filterModel, items: [] })
  }

  const filterUngroupedColumns = React.useCallback(
    (args: FilterColumnsArgs) => {
      return getUngroupedColumns().map((col) => col.field)
    },
    [filterGroups, getUngroupedColumns, items],
  )

  React.useEffect(() => {
    if (
      logicOperators.length > 0 &&
      filterModel.logicOperator &&
      !logicOperators.includes(filterModel.logicOperator)
    ) {
      applyFilterLogicOperator(logicOperators[0]!)
    }
  }, [logicOperators, applyFilterLogicOperator, filterModel.logicOperator])

  /*
  React.useEffect(() => {
    if (items.length > 0) {
      lastFilterRef.current!.focus()
    }
  }, [items.length])
  */

  return (
    <GridPanelWrapper ref={ref} {...other}>
      <GridPanelContent>
        {/* Render ungrouped filters as before */}
        {getUngroupedItems().map((item, index) => {
          const group = getGroupForItem(item)
          return group ? (
            <>
              <GridFilterForm
                key={item.id ?? index}
                item={item}
                applyFilterChanges={applyFilter}
                deleteFilter={deleteFilter}
                hasMultipleFilters={hasMultipleFilters}
                showMultiFilterOperators={index > 0}
                multiFilterOperator={filterModel.logicOperator}
                disableMultiFilterOperator={index !== 1}
                applyMultiFilterOperatorChanges={applyFilterLogicOperator}
                focusElementRef={null}
                logicOperators={logicOperators}
                columnsSort={columnsSort}
                filterColumns={filterUngroupedColumns}
                {...filterFormProps}
              />
              {
                <Accordion
                  key={group.label}
                  expanded={expandedGroup === group.label}
                  onChange={(_, expanded) =>
                    setExpandedGroup(expanded ? group.label : null)
                  }
                  sx={{
                    m: '10px 10px 10px 10px',
                    '&.Mui-expanded': {
                      m: '10px 10px 10px 10px',
                      minHeight: '120px',
                    },
                    '&.MuiAccordion-root.Mui-expanded': {
                      mb: '10px',
                      minHeight: '120px',
                      overflowY: 'auto',
                    },
                    borderRadius: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 28,
                      px: 1,
                      '&.MuiAccordionSummary-root': { minHeight: 28, px: 1 },
                      '& .MuiAccordionSummary-content': {
                        my: 0,
                        py: 0,
                        px: 0,
                        minHeight: 0,
                        alignItems: 'center',
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        margin: 0,
                        minHeight: 0,
                      },
                    }}
                  >
                    {group.label}
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      py: 0.25,
                      px: 0.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5, // reduce vertical gap between filter forms and button
                    }}
                  >
                    {getItemsForGroup(group).map((item, index) => (
                      <GridFilterForm
                        key={item.id ?? index}
                        item={item}
                        applyFilterChanges={applyFilter}
                        deleteFilter={deleteFilter}
                        hasMultipleFilters={hasMultipleFilters}
                        showMultiFilterOperators={false}
                        multiFilterOperator={filterModel.logicOperator}
                        disableMultiFilterOperator={index !== 1}
                        applyMultiFilterOperatorChanges={
                          applyFilterLogicOperator
                        }
                        focusElementRef={null}
                        logicOperators={logicOperators}
                        columnsSort={columnsSort}
                        filterColumns={(args: FilterColumnsArgs) => {
                          return getGroupColumns(group).map((col) => col.field)
                        }}
                        {...filterFormProps}
                      />
                    ))}
                    {/* Add button for this group */}
                    {!disableAddFilterButton && (
                      <rootProps.slots.baseButton
                        onClick={() => {
                          // Add new filter for this group
                          const groupColumns = filterableColumns.filter((col) =>
                            group.fields.includes(col.field),
                          )

                          const newFilter = getGridFilter(groupColumns[0]!)
                          apiRef.current.upsertFilterItems([
                            ...items,
                            newFilter,
                          ])
                        }}
                        startIcon={<rootProps.slots.filterPanelAddIcon />}
                        {...rootProps.slotProps?.baseButton}
                        sx={{
                          ...rootProps.slotProps?.baseButton?.sx,
                          ml: 'auto',
                        }}
                      >
                        {apiRef.current.getLocaleText('filterPanelAddFilter')}
                      </rootProps.slots.baseButton>
                    )}
                  </AccordionDetails>
                </Accordion>
              }
            </>
          ) : (
            <GridFilterForm
              key={item.id ?? index}
              item={item}
              applyFilterChanges={applyFilter}
              deleteFilter={deleteFilter}
              hasMultipleFilters={hasMultipleFilters}
              showMultiFilterOperators={index > 0}
              multiFilterOperator={filterModel.logicOperator}
              disableMultiFilterOperator={index !== 1}
              applyMultiFilterOperatorChanges={applyFilterLogicOperator}
              focusElementRef={null}
              logicOperators={logicOperators}
              columnsSort={columnsSort}
              filterColumns={filterUngroupedColumns}
              {...filterFormProps}
            />
          )
        })}
      </GridPanelContent>
      {/* Footer as before */}
      {!rootProps.disableMultipleColumnsFiltering &&
      !(disableAddFilterButton && disableRemoveAllButton) ? (
        <GridPanelFooter>
          {!disableAddFilterButton ? (
            <rootProps.slots.baseButton
              onClick={addNewFilter}
              startIcon={<rootProps.slots.filterPanelAddIcon />}
              {...rootProps.slotProps?.baseButton}
            >
              {apiRef.current.getLocaleText('filterPanelAddFilter')}
            </rootProps.slots.baseButton>
          ) : (
            <span />
          )}
          {!disableRemoveAllButton ? (
            <rootProps.slots.baseButton
              onClick={handleRemoveAll}
              startIcon={<rootProps.slots.filterPanelRemoveAllIcon />}
              {...rootProps.slotProps?.baseButton}
            >
              {apiRef.current.getLocaleText('filterPanelRemoveAll')}
            </rootProps.slots.baseButton>
          ) : null}
        </GridPanelFooter>
      ) : null}
    </GridPanelWrapper>
  )
})

export { GridGroupFilterPanel }
